import type { ApiResponse, PaginatedResult, PaginationInfo } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export class ApiError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;

  constructor(message: string, code: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

function getErrorDetails(error: ApiResponse<unknown>['error']): {
  message: string;
  code: string;
  details?: Record<string, unknown>;
} {
  if (typeof error === 'string') {
    return { message: error, code: 'UNKNOWN_ERROR' };
  }

  return {
    message: error?.message || 'An error occurred',
    code: error?.code || 'UNKNOWN_ERROR',
    details: error?.details,
  };
}

async function handleEnvelope<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type');

  if (!contentType?.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError('Network error', 'NETWORK_ERROR', response.status);
    }
    return { success: true, data: {} as T };
  }

  const data = (await response.json()) as ApiResponse<T>;

  if (!response.ok || !data.success) {
    const error = getErrorDetails(data.error);
    throw new ApiError(error.message, error.code, response.status, error.details);
  }

  return data;
}

async function handleResponse<T>(response: Response): Promise<T> {
  const envelope = await handleEnvelope<T>(response);
  return envelope.data as T;
}

export function buildApiUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

export async function apiGet<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;
  const url = buildApiUrl(endpoint, params);

  const response = await fetch(url, {
    ...fetchOptions,
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
  });

  return handleResponse<T>(response);
}

export async function apiGetPaginated<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<PaginatedResult<T>> {
  const { params, ...fetchOptions } = options;
  const url = buildApiUrl(endpoint, params);

  const response = await fetch(url, {
    ...fetchOptions,
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
  });

  const envelope = await handleEnvelope<T>(response);
  const data = envelope.data as T;
  const inferredTotal = Array.isArray(data) ? data.length : 0;
  const fallback: PaginationInfo = {
    page: 1,
    pageSize: Math.max(1, inferredTotal),
    total: inferredTotal,
    totalPages: 1,
  };

  return {
    data,
    pagination: envelope.pagination ?? fallback,
  };
}

export async function apiPost<T>(
  endpoint: string,
  body?: unknown,
  options: RequestOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;
  const url = buildApiUrl(endpoint, params);

  const response = await fetch(url, {
    ...fetchOptions,
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  return handleResponse<T>(response);
}

export async function apiPut<T>(
  endpoint: string,
  body?: unknown,
  options: RequestOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;
  const url = buildApiUrl(endpoint, params);

  const response = await fetch(url, {
    ...fetchOptions,
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  return handleResponse<T>(response);
}

export async function apiPatch<T>(
  endpoint: string,
  body?: unknown,
  options: RequestOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;
  const url = buildApiUrl(endpoint, params);

  const response = await fetch(url, {
    ...fetchOptions,
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  return handleResponse<T>(response);
}

export async function apiDelete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;
  const url = buildApiUrl(endpoint, params);

  const response = await fetch(url, {
    ...fetchOptions,
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
  });

  return handleResponse<T>(response);
}
